import type { PhoneCountry } from '../../lib/phone/countries'
import { parseE164, buildE164, validatePhone } from '../../lib/phone/utils'


// Handle country select
export function handleCountrySelect(
  country: PhoneCountry,
  setSelectedCountry: React.Dispatch<React.SetStateAction<PhoneCountry>>,
  setNationalNumber: React.Dispatch<React.SetStateAction<string>>,
  setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  setFocusedIndex: React.Dispatch<React.SetStateAction<number>>,
  inputRef: React.MutableRefObject<HTMLInputElement | null>,
  onChange?: (value: string | null, meta: { countryIso2: string; callingCode: string; national: string; isValid: boolean }) => void
) {
  setSelectedCountry(country)
  setNationalNumber('') // Clear national number when country changes
  setIsDropdownOpen(false)
  setSearchQuery('')
  setFocusedIndex(-1)
  inputRef.current?.focus()

  // Emit change with empty national
  if (onChange) {
    onChange(null, {
      countryIso2: country.iso2,
      callingCode: country.callingCode,
      national: '',
      isValid: false,
    })
  }
}

// Handle input change
export function handleInputChange(
  unmasked: string,
  selectedCountry: PhoneCountry,
  countries: PhoneCountry[],
  setSelectedCountry: React.Dispatch<React.SetStateAction<PhoneCountry>>,
  setNationalNumber: React.Dispatch<React.SetStateAction<string>>,
  onChange?: (value: string | null, meta: { countryIso2: string; callingCode: string; national: string; isValid: boolean }) => void
) {
  // Remove any non-digits for processing
  const digitsOnly = unmasked.replace(/\D/g, '')
  
  // Check if the input contains a full phone number with country code (from autofill)
  if (digitsOnly && digitsOnly.length > selectedCountry.nationalDigits) {
    // Try to parse as E.164 format
    let fullNumber = digitsOnly
    
    // Ensure it starts with + for E.164 parsing
    if (!fullNumber.startsWith('+')) {
      fullNumber = '+' + fullNumber
    }
    
    // Try to parse using libphonenumber-js first (supports all countries)
    try {
      const parsed = parseE164(fullNumber, countries)
      if (parsed.country && parsed.national) {
        // Successfully parsed - update country and national number
        setSelectedCountry(parsed.country)
        setNationalNumber(parsed.national)
        
        const e164 = buildE164(parsed.country, parsed.national)
        const isValid = e164 ? validatePhone(e164) : false
        
        if (onChange) {
          onChange(e164, {
            countryIso2: parsed.country.iso2,
            callingCode: parsed.country.callingCode,
            national: parsed.national,
            isValid,
          })
        }
        return
      }
    } catch {
      // If parsing fails, try manual matching against known countries
      // Try to match against known country codes (sorted by length, longest first)
      const sortedCountries = [...countries].sort((a, b) => b.callingCode.length - a.callingCode.length)
      
      for (const country of sortedCountries) {
        const code = country.callingCode
        if (digitsOnly.startsWith(code)) {
          const national = digitsOnly.slice(code.length)
          if (national.length <= country.nationalDigits && national.length > 0) {
            setSelectedCountry(country)
            setNationalNumber(national)
            
            const e164 = buildE164(country, national)
            const isValid = e164 ? validatePhone(e164) : false
            
            if (onChange) {
              onChange(e164, {
                countryIso2: country.iso2,
                callingCode: country.callingCode,
                national: national,
                isValid,
              })
            }
            return
          }
        }
      }
    }
  }
  
  // Normal flow: enforce max length for national number only
  const maxLength = selectedCountry.nationalDigits
  const digits = digitsOnly.slice(0, maxLength)
  setNationalNumber(digits)

  // Build E.164 and emit
  const e164 = buildE164(selectedCountry, digits)
  const isValid = e164 ? validatePhone(e164) : false

  if (onChange) {
    onChange(e164, {
      countryIso2: selectedCountry.iso2,
      callingCode: selectedCountry.callingCode,
      national: digits,
      isValid,
    })
  }
}
